import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * In-place hero carousel + fullscreen viewer.
 *  - `HeroCarousel` replaces the hero image: swipe / arrow-click rotates photos in
 *    place, dots + a "1/5" pill show there are more, and tapping the photo enlarges it.
 *  - `usePhotoLightbox` provides the fullscreen enlarge viewer (arrows, counter, close).
 */
export function usePhotoLightbox(photos = [], name = "") {
    const [idx, setIdx] = useState(-1);
    const isOpen = idx >= 0 && photos.length > 0;

    const open = useCallback((i) => setIdx(i), []);
    const close = useCallback(() => setIdx(-1), []);
    const go = useCallback((delta) => {
        setIdx((c) => {
            const n = photos.length;
            return n ? (((c + delta) % n) + n) % n : c;
        });
    }, [photos.length]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            else if (e.key === "ArrowRight") go(1);
            else if (e.key === "ArrowLeft") go(-1);
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }, [isOpen, close, go]);

    const node = isOpen ? (
        <div className="fixed inset-0 z-[2000] bg-black flex flex-col" data-testid="photo-lightbox" onClick={close}>
            {/* Top bar — counter + a clearly visible circular close button */}
            <div className="relative flex items-center justify-between px-4 py-3 shrink-0 z-10">
                <span className="text-sm text-white/90 tracking-wide" data-testid="lightbox-counter">{idx + 1} / {photos.length}</span>
                <button type="button" data-testid="lightbox-close" onClick={close} aria-label="Close"
                    className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors">
                    <X className="h-6 w-6" />
                </button>
            </div>
            <div className="relative flex-1 flex items-center justify-center px-4 min-h-0" onClick={(e) => e.stopPropagation()}>
                {photos.length > 1 && (
                    <button type="button" data-testid="lightbox-prev" onClick={() => go(-1)} aria-label="Previous"
                        className="absolute left-2 sm:left-4 z-10 h-12 w-12 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors">
                        <ChevronLeft className="h-7 w-7" />
                    </button>
                )}
                <img src={photos[idx]} alt={`${name} photo ${idx + 1}`} className="max-h-full max-w-full object-contain select-none" data-testid="lightbox-image" />
                {photos.length > 1 && (
                    <button type="button" data-testid="lightbox-next" onClick={() => go(1)} aria-label="Next"
                        className="absolute right-2 sm:right-4 z-10 h-12 w-12 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors">
                        <ChevronRight className="h-7 w-7" />
                    </button>
                )}
            </div>
            {photos.length > 1 && (
                <div className="flex gap-2 justify-center p-4 overflow-x-auto shrink-0" onClick={(e) => e.stopPropagation()}>
                    {photos.map((src, i) => (
                        <button key={`${src}-${i}`} type="button" onClick={() => setIdx(i)} data-testid={`lightbox-thumb-${i}`}
                            className={`h-12 w-20 shrink-0 overflow-hidden border-2 transition-all ${i === idx ? "border-[#ff4500]" : "border-transparent opacity-50 hover:opacity-100"}`}>
                            <img src={src} alt="" className="h-full w-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    ) : null;

    return { open, node, hasPhotos: photos.length > 0 };
}

/** Swipeable hero photo carousel rendered in place of the static hero image.
 *  `badge` is overlaid (e.g. the logo badge); tapping the photo calls `onOpen(index)`. */
export function HeroCarousel({ photos = [], name = "", onOpen, badge = null, testid = "hero-carousel" }) {
    const [i, setI] = useState(0);
    const touchX = useRef(null);
    const n = photos.length;

    const go = useCallback((d) => setI((c) => (n ? (((c + d) % n) + n) % n : 0)), [n]);
    useEffect(() => { if (i >= n) setI(0); }, [n, i]);
    if (n === 0) return null;

    const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
    const onTouchEnd = (e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touchX.current = null;
    };

    return (
        <div className="relative aspect-[16/9] overflow-hidden bg-[#0a0a0a] select-none" data-testid={testid}
            onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <img
                src={photos[i]}
                alt={`${name} photo ${i + 1}`}
                data-testid={`${testid}-image`}
                onClick={() => onOpen && onOpen(i)}
                className="block w-full h-full object-cover cursor-zoom-in"
            />
            {badge}
            {n > 1 && (
                <>
                    <button type="button" data-testid={`${testid}-prev`} aria-label="Previous photo"
                        onClick={(e) => { e.stopPropagation(); go(-1); }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center transition-colors">
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button type="button" data-testid={`${testid}-next`} aria-label="Next photo"
                        onClick={(e) => { e.stopPropagation(); go(1); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center transition-colors">
                        <ChevronRight className="h-6 w-6" />
                    </button>
                    <span className="absolute top-3 right-3 z-10 text-[11px] font-semibold text-white bg-black/55 px-2 py-1 rounded-full tracking-wide" data-testid={`${testid}-counter`}>
                        {i + 1} / {n}
                    </span>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
                        {photos.map((_, d) => (
                            <button key={d} type="button" aria-label={`Photo ${d + 1}`} data-testid={`${testid}-dot-${d}`}
                                onClick={(e) => { e.stopPropagation(); setI(d); }}
                                className={`h-2 rounded-full transition-all ${d === i ? "w-5 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
